import React, { useState, useEffect, useCallback } from 'react';
import { Truck, RefreshCw, Eye } from 'lucide-react';
import apiService from '../apiService';
import { useAdmin } from '../context/AdminContext';
import Img from '../components/Img';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

const CAT_LABELS = { standard: 'Mbiyo', confort: 'Confort', premium: 'Premium', moto: 'Moto' };
const ACTIVITY_META = {
  ride: { label: 'En course', color: '#FF7800' },
  delivery: { label: 'En livraison', color: '#F54119' },
  parcel: { label: 'Colis en cours', color: '#C9350F' },
};
const REFRESH_MS = 30000;

const roleTypeLabel = (d) => {
  const roles = (d.approved_roles && d.approved_roles.length ? d.approved_roles : d.requested_roles) || ['driver'];
  const isDriver = roles.includes('driver'), isDeliverer = roles.includes('deliverer');
  if (isDriver && isDeliverer) return 'Chauffeur + Livreur';
  if (isDeliverer) return 'Livreur';
  return 'Chauffeur';
};

// « il y a 3 min » / « 2 h 05 » depuis une date.
const sinceLabel = (d) => {
  if (!d) return null;
  const min = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h ${String(min % 60).padStart(2, '0')}`;
  return `il y a ${Math.floor(h / 24)} j`;
};

export default function OnlineDriversPage() {
  const [freshMin, setFreshMin] = useState(10); // seuil « position récente » du dispatch (réglage admin)
  const { setSelectedDriver } = useAdmin();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all'); // 'all' | 'free' | 'busy'
  const [cityFilter, setCityFilter] = useState(''); // '' = toutes les villes
  const cities = useCities();

  const load = useCallback(async () => {
    try {
      const res = await apiService.get('/admin/drivers/online', { userType: 'admin' });
      if (res?.position_fresh_min) setFreshMin(Number(res.position_fresh_min) || 10);
      if (res?.success) { setDrivers(res.drivers || []); setUpdatedAt(new Date()); }
    } catch (err) { console.error('Erreur chauffeurs en ligne:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const inCity = drivers.filter(d => !cityFilter || d.city_slug === cityFilter);
  const visible = inCity.filter(d => activityFilter === 'all' || (activityFilter === 'free' ? !d.activity : !!d.activity));
  const freeCount = inCity.filter(d => !d.activity).length;

  // Position jugée ancienne au-delà du seuil du dispatch (Paramètres → Dispatch course, 10 min par défaut) :
  // le chauffeur reste en ligne et sollicitable, mais passe après ceux dont la position est récente.
  const positionCell = (d) => {
    const at = d.location?.updated_at;
    if (!at || d.location?.lat == null) return <span style={{ color: '#9AA0A8' }} title="Aucune position reçue : classé après les positions récentes">Inconnue</span>;
    const stale = Date.now() - new Date(at).getTime() > freshMin * 60000;
    return (
      <a href={`https://www.google.com/maps?q=${d.location.lat},${d.location.lng}`} target="_blank" rel="noreferrer"
        style={{ color: stale ? '#B45309' : '#166534', fontWeight: 600, textDecoration: 'none' }} title={stale ? `Position ancienne (> ${freshMin} min) : classé après les positions récentes — voir sur Google Maps` : 'Voir sur Google Maps'}>
        {stale ? '⚠ ' : '● '}{sinceLabel(at)}
      </a>
    );
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Truck size={28} /> Chauffeurs en ligne <span className="dash-live-dot" />
        </h1>
        <div className="page-actions">
          <select className="filter-select" value={activityFilter} onChange={(e) => setActivityFilter(e.target.value)}>
            <option value="all">Tous ({inCity.length})</option>
            <option value="free">Libres ({freeCount})</option>
            <option value="busy">Occupés ({inCity.length - freeCount})</option>
          </select>
          <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} />
          <button className="btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshCw size={16} /> Actualiser
          </button>
        </div>
      </div>
      <p style={{ margin: '-0.5rem 0 1rem', fontSize: '0.8rem', color: '#9AA0A8' }}>
        En ligne = disponible et approuvé (ce que le dispatch sollicite) · rafraîchi toutes les 30 s{updatedAt ? ` · dernière mise à jour ${updatedAt.toLocaleTimeString('fr-FR')}` : ''}
      </p>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Chauffeur</th><th>Téléphone</th><th>Ville</th><th>Type</th><th>Catégories</th><th>Véhicule</th>
                <th>Activité</th><th>Position</th><th>En ligne depuis</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucun chauffeur en ligne{cityFilter ? ` à ${cityLabel(cities, cityFilter)}` : ''} en ce moment</td></tr>
              )}
              {visible.map(driver => {
                const act = driver.activity ? ACTIVITY_META[driver.activity.kind] : null;
                return (
                  <tr key={driver.driver_id}>
                    <td>
                      <div className="driver-cell" onClick={() => setSelectedDriver(driver)} style={{ cursor: 'pointer' }}>
                        {driver.photo ? (
                          <Img src={driver.photo} alt={driver.name} className="table-avatar" />
                        ) : (
                          <div className="table-avatar no-photo">{driver.name?.charAt(0)}</div>
                        )}
                        <span style={{ textDecoration: 'underline', color: '#C9350F', fontWeight: '500' }}>{driver.name}</span>
                      </div>
                    </td>
                    <td>{driver.phone}</td>
                    <td>{cityLabel(cities, driver.city_slug)}</td>
                    <td>{roleTypeLabel(driver)}</td>
                    <td>{(driver.categories || ['standard']).map(c => CAT_LABELS[c] || c).join(', ')}</td>
                    <td>{driver.vehicle?.brand} {driver.vehicle?.model} {driver.vehicle?.plate && <span className="plate-badge">{driver.vehicle.plate}</span>}</td>
                    <td>
                      {act ? (
                        <span title={driver.activity.ref} style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: `${act.color}20`, color: act.color, whiteSpace: 'nowrap' }}>{act.label}</span>
                      ) : (
                        <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', background: '#ECFDF5', color: '#166534' }}>Libre</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{positionCell(driver)}</td>
                    <td style={{ fontSize: '0.8rem', color: '#5C636E', whiteSpace: 'nowrap' }}>{sinceLabel(driver.last_availability_change) || '—'}</td>
                    <td><button className="icon-btn" onClick={() => setSelectedDriver(driver)} title="Voir le chauffeur"><Eye size={18} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
